import { useEffect, useState } from "react";
import { useLocale, type Locale } from "../lib/i18n";

interface Props {
  streak: number;
  locale: Locale;
  setLocale: (l: Locale) => void;
  isLoggedIn: boolean;
  displayName: string | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onProfile: () => void;
  onHelp: () => void;
}

export function HeaderMenu({
  streak,
  isLoggedIn,
  displayName,
  onSignIn,
  onSignOut,
  onProfile,
  onHelp,
}: Props) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="תפריט"
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity"
        style={{
          color: "var(--parchment-soft)",
          opacity: 0.7,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div
            role="menu"
            className="absolute end-0 top-full mt-2 z-50 w-56 rounded-xl p-2 shadow-2xl"
            style={{
              background: "var(--parchment-soft)",
              border: "1px solid var(--parchment-edge)",
              color: "var(--ink)",
            }}
          >
            {streak > 0 && (
              <div
                className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
                style={{
                  background: "rgba(26,33,66,0.05)",
                }}
              >
                <span
                  style={{
                    fontFamily: "'Heebo', sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#8a7340",
                  }}
                >
                  רצף
                </span>
                <span
                  className="font-serif tabular-nums"
                  style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}
                >
                  {streak}
                </span>
              </div>
            )}

            <MenuItem
              label="עזרה"
              onClick={() => {
                onHelp();
                close();
              }}
            />

            <div className="h-px my-1.5" style={{ background: "var(--parchment-edge)" }} />

            {isLoggedIn ? (
              <>
                {displayName && (
                  <MenuItem
                    label={displayName}
                    onClick={() => {
                      onProfile();
                      close();
                    }}
                  />
                )}
                <MenuItem
                  label={t("common.signOut")}
                  tone="danger"
                  onClick={() => {
                    onSignOut();
                    close();
                  }}
                />
              </>
            ) : (
              <MenuItem
                label={t("common.signIn")}
                tone="gold"
                onClick={() => {
                  onSignIn();
                  close();
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  tone,
}: {
  label: string;
  onClick: () => void;
  tone?: "gold" | "danger";
}) {
  const color =
    tone === "gold" ? "#8a6a1a" : tone === "danger" ? "#a04040" : "var(--ink)";
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full text-start px-3 py-2 rounded-lg text-sm transition-colors truncate"
      style={{
        fontFamily: "'Heebo', sans-serif",
        fontWeight: tone === "gold" ? 700 : 500,
        color,
      }}
    >
      {label}
    </button>
  );
}
